/* ============================================================================
   domain_selector — the visual Domain Builder dialog (foundation.presentation
   Enhancement 13). React port of the frozen mockup:
   roadmap/foundation/presentation/enhancements/assets/13-domain-selector-mockup/

   Field CHAINS (drill through relations) → operator → value. enum / m2o / m2m /
   o2m support `in` with multi-select chips. AND/OR groups + nesting, `$principal.*`
   dynamic values, date/datetime `between` ranges. Live readable preview + a
   read-only Code tab showing the serialized EDE domain.
   ========================================================================== */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import * as Dialog from "@radix-ui/react-dialog";
import {
    Check, ChevronDown, ChevronRight, Code, Eye, Filter, Layers, List, Loader2, Pencil, Plus, Search, SlidersHorizontal, X,
} from "lucide-react";
import type { Condition, Domain, Group, Node, RangeValue } from "./types";
import { isRange } from "./types";
import { serializeDomain, parseDomain, conditionCount } from "./serialize";
import { opsForType, opMeta, type ValueMode } from "./operators";
import {
    DYN_LABEL, STATIC_M2O, m2oLabel, modelAtDrill, walkChain, type ChainMeta, type ModelSpec, type SpecGraph,
} from "./fieldspec";

interface PickerState {
    path: number[];
    drill: string[];
    x: number;
    y: number;
}

/** Resolve one model's spec — Slice 3 wires this to the live introspection
 *  read (`GET /api/web/_model/{model_key}/fields`); tests inject a static
 *  fetcher over `STATIC_SPEC`. Returns null when the model is unknown. */
export type SpecLoader = (modelKey: string) => Promise<ModelSpec | null>;

export interface DomainBuilderDialogProps {
    open: boolean;
    /** Target model key the domain filters on. */
    model: string;
    /** Pre-seeded field-spec graph. Optional once `loadSpec` is provided —
     *  specs then arrive asynchronously and accumulate into this graph. When
     *  neither is given the builder seeds an empty graph. */
    spec?: SpecGraph;
    /** Async spec resolver — fetched for the target model on open and for each
     *  related model the user drills into. When omitted the dialog uses `spec`
     *  alone (Slice-1 static behaviour). */
    loadSpec?: SpecLoader;
    /** Async match-count resolver (Scope E). When provided, the footer shows
     *  "matches N" for the live serialized domain (debounced). Failures degrade
     *  silently — the builder keeps working without a count. */
    countFor?: (domain: Domain) => Promise<number | null>;
    /** Async option resolver for relational (m2o/m2m/o2m) value chips —
     *  `[id, label]` tuples for a target model. When omitted the chips use the
     *  static `STATIC_M2O` sample (Slice-1 / test behaviour). */
    loadOptions?: (relModel: string) => Promise<Array<[string, string]>>;
    /** Initial domain (EDE prefix notation). */
    value: Domain;
    onApply: (domain: Domain) => void;
    onClose: () => void;
}

type BuilderMode = "visual" | "readable" | "code";

const samePath = (a: number[], b: number[]): boolean => a.length === b.length && a.every((v, i) => v === b[i]);

/* ---- tree navigation + defaults --------------------------------------- */

function nodeAt(root: Group, path: number[]): Node {
    let n: Node = root;
    for (const i of path) {
        if (n.kind !== "group") throw new Error("path traverses a leaf");
        n = n.items[i];
    }
    return n;
}

function defaultValue(spec: SpecGraph, model: string, chain: string, op: string): Condition["value"] {
    const meta = walkChain(spec, model, chain);
    const vm = opMeta(meta.type, op).vmode;
    if (vm === "none") return null;
    if (vm === "many") return [];
    if (vm === "one") return meta.rel ? (STATIC_M2O[meta.rel]?.[0]?.[0] ?? "") : (meta.sel?.[0] ?? "");
    if (vm === "num") return 0;
    if (vm === "bool") return true;
    if (vm === "range") {
        return meta.type === "datetime"
            ? { from: "", to: "" }
            : { from: "", to: "" };
    }
    if (vm === "date") return "";
    return "";
}

export function DomainBuilderDialog({ open, model, spec: specProp, loadSpec, countFor, loadOptions, value, onApply, onClose }: DomainBuilderDialogProps) {
    const [tree, setTree] = useState<Group>(() => safeParse(value));
    const [mode, setMode] = useState<BuilderMode>("visual");
    const [picker, setPicker] = useState<PickerState | null>(null);
    const [fpQuery, setFpQuery] = useState("");

    // Accumulated spec graph: seeded from the `spec` prop (static / test path)
    // and grown by `loadSpec` as the target + drilled relation models arrive.
    const [spec, setSpec] = useState<SpecGraph>(() => ({ ...(specProp ?? {}) }));
    // Keep in sync if the parent passes a (memoized) static graph.
    const specPropRef = useRef(specProp);
    if (specProp && specProp !== specPropRef.current) {
        specPropRef.current = specProp;
        setSpec((prev) => ({ ...specProp, ...prev }));
    }
    // Track in-flight / requested model fetches so each model loads at most once.
    const requested = useRef<Set<string>>(new Set());
    const [loadingModels, setLoadingModels] = useState<Set<string>>(() => new Set());

    const ensureSpec = useCallback((modelKey: string) => {
        if (!loadSpec || !modelKey || spec[modelKey] || requested.current.has(modelKey)) return;
        requested.current.add(modelKey);
        setLoadingModels((s) => new Set(s).add(modelKey));
        void loadSpec(modelKey)
            .then((ms) => {
                if (ms) setSpec((prev) => ({ ...prev, [modelKey]: ms }));
            })
            .catch(() => { /* degrade — picker shows no fields for this model */ })
            .finally(() => setLoadingModels((s) => { const n = new Set(s); n.delete(modelKey); return n; }));
    }, [loadSpec, spec]);

    // Fetch the target model's spec on open.
    useEffect(() => {
        if (open) ensureSpec(model);
    }, [open, model, ensureSpec]);

    const targetLoaded = !!spec[model];
    const targetLoading = loadingModels.has(model) || (!targetLoaded && !!loadSpec && !specProp?.[model]);

    // Re-seed the tree when re-opened against a different stored value.
    const seedRef = useRef<Domain>(value);
    if (open && seedRef.current !== value) {
        seedRef.current = value;
        setTree(safeParse(value));
    }

    // ── match count (Scope E) — debounced, degrades silently ────────────────
    const [matchCount, setMatchCount] = useState<number | null>(null);
    const serialized = useMemo(() => serializeDomain(tree), [tree]);
    const serializedKey = useMemo(() => JSON.stringify(serialized), [serialized]);
    useEffect(() => {
        if (!open || !countFor) { setMatchCount(null); return; }
        let cancelled = false;
        const handle = setTimeout(() => {
            void countFor(serialized)
                .then((n) => { if (!cancelled) setMatchCount(n); })
                .catch(() => { if (!cancelled) setMatchCount(null); });
        }, 300);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [open, countFor, serializedKey, serialized]);

    // ── relational value options (Scope C) — fetched per target model, cached ──
    const [relOptions, setRelOptions] = useState<Record<string, Array<[string, string]>>>({});
    const optRequested = useRef<Set<string>>(new Set());

    const ensureOptions = useCallback((relModel: string) => {
        if (!loadOptions || !relModel || relOptions[relModel] || optRequested.current.has(relModel)) return;
        optRequested.current.add(relModel);
        void loadOptions(relModel)
            .then((opts) => { if (opts && opts.length) setRelOptions((prev) => ({ ...prev, [relModel]: opts })); })
            .catch(() => { /* degrade — chips fall back to STATIC_M2O / typed ids */ });
    }, [loadOptions, relOptions]);

    // Walk the tree's conditions and pre-fetch options for every relational
    // terminal field in use, so the value chips show real records.
    useEffect(() => {
        if (!open || !loadOptions) return;
        const seen = new Set<string>();
        const visit = (node: Node): void => {
            if (node.kind === "group") { node.items.forEach(visit); return; }
            const rel = walkChain(spec, model, node.field).rel;
            if (rel && !seen.has(rel)) { seen.add(rel); ensureOptions(rel); }
        };
        visit(tree);
    }, [open, loadOptions, serializedKey, spec, model, tree, ensureOptions]);

    /** Option tuples for a value editor: live records for relations (falling
     *  back to the static sample), enum labels otherwise. */
    const optsFor = useCallback((m: ChainMeta): Array<[string, string]> => {
        if (m.rel) {
            ensureOptions(m.rel);
            return relOptions[m.rel] ?? STATIC_M2O[m.rel] ?? [];
        }
        return (m.sel ?? []).map((s) => [s, s] as [string, string]);
    }, [relOptions, ensureOptions]);

    /** Resolve a stored relational id to its label (live cache → static → id). */
    const relLabelFor = useCallback((rel: string, id: string): string => {
        const hit = (relOptions[rel] ?? []).find((o) => o[0] === id);
        if (hit) return hit[1];
        return m2oLabel(rel, id);
    }, [relOptions]);

    const firstField = useCallback(() => spec[model]?.fields[0]?.name ?? "name", [spec, model]);

    const newCond = useCallback((): Condition => {
        const f = firstField();
        const type = walkChain(spec, model, f).type;
        const op = opsForType(type)[0].op;
        return { kind: "cond", field: f, op, value: defaultValue(spec, model, f, op) };
    }, [spec, model, firstField]);

    /** Apply a mutation against a cloned tree (structuredClone — plain data). */
    const edit = useCallback((mutator: (root: Group) => void) => {
        setTree((prev) => {
            const draft = structuredClone(prev);
            mutator(draft);
            return draft;
        });
    }, []);

    const domain = serialized;
    const condCount = useMemo(() => conditionCount(tree), [tree]);

    const meta = (node: Condition) => walkChain(spec, model, node.field);

    /* ---- field-chain picker --------------------------------------------- */
    const openPicker = (path: number[], el: HTMLElement) => {
        if (picker && samePath(picker.path, path)) {
            setPicker(null);
            return;
        }
        const r = el.getBoundingClientRect();
        setFpQuery("");
        setPicker({ path, drill: [], x: Math.min(r.left, window.innerWidth - 336), y: r.bottom });
    };

    const chooseField = (name: string) => {
        if (!picker) return;
        const chain = [...picker.drill, name].join(".");
        edit((root) => {
            const c = nodeAt(root, picker.path) as Condition;
            c.field = chain;
            const type = walkChain(spec, model, chain).type;
            c.op = opsForType(type)[0].op;
            c.dyn = false;
            c.value = defaultValue(spec, model, chain, c.op);
        });
        setPicker(null);
    };

    /* ---- value editor --------------------------------------------------- */
    const renderValue = (path: number[], c: Condition) => {
        const m = meta(c);
        const vm: ValueMode = opMeta(m.type, c.op).vmode;
        if (vm === "none") return <div className="noval">no value needed</div>;
        const canDyn = !!m.dyn;

        if (c.dyn) {
            const opts = [m.dyn!];
            if (m.type === "date" || m.type === "datetime") opts.push("$today");
            return (
                <div className="dynwrap">
                    <div className="dynval">
                        <span className="pfx">$</span>
                        <select
                            value={String(c.value ?? "")}
                            onChange={(e) => edit((r) => { (nodeAt(r, path) as Condition).value = e.target.value; })}
                        >
                            {opts.map((o) => <option key={o} value={o}>{DYN_LABEL[o] ?? o}</option>)}
                        </select>
                    </div>
                    {dynToggle(path, true)}
                </div>
            );
        }

        let inner: React.ReactNode;
        if (vm === "text") {
            inner = (
                <input
                    className="cond-in" value={String(c.value ?? "")} placeholder="value…"
                    onChange={(e) => edit((r) => { (nodeAt(r, path) as Condition).value = e.target.value; })}
                />
            );
        } else if (vm === "num") {
            inner = (
                <input
                    className="cond-in" type="number" value={String(c.value ?? "")}
                    onChange={(e) => edit((r) => { (nodeAt(r, path) as Condition).value = Number(e.target.value); })}
                />
            );
        } else if (vm === "date") {
            const it = m.type === "datetime" ? "datetime-local" : "date";
            inner = (
                <input
                    className="cond-in" type={it} value={String(c.value ?? "")}
                    onChange={(e) => edit((r) => { (nodeAt(r, path) as Condition).value = e.target.value; })}
                />
            );
        } else if (vm === "range") {
            const it = m.type === "datetime" ? "datetime-local" : "date";
            const rv: RangeValue = isRange(c.value) ? c.value : { from: "", to: "" };
            inner = (
                <div className="range">
                    <input
                        className="cond-in" type={it} value={rv.from}
                        onChange={(e) => edit((r) => { const c2 = nodeAt(r, path) as Condition; c2.value = { ...asRange(c2.value), from: e.target.value }; })}
                    />
                    <span className="rsep">→</span>
                    <input
                        className="cond-in" type={it} value={rv.to}
                        onChange={(e) => edit((r) => { const c2 = nodeAt(r, path) as Condition; c2.value = { ...asRange(c2.value), to: e.target.value }; })}
                    />
                </div>
            );
        } else if (vm === "bool") {
            inner = (
                <select
                    className="cond-in" value={c.value === true ? "true" : "false"}
                    onChange={(e) => edit((r) => { (nodeAt(r, path) as Condition).value = e.target.value === "true"; })}
                >
                    <option value="true">true</option>
                    <option value="false">false</option>
                </select>
            );
        } else if (vm === "one") {
            const opts = optsFor(m);
            inner = (
                <select
                    className="cond-in" value={String(c.value ?? "")}
                    onChange={(e) => edit((r) => { (nodeAt(r, path) as Condition).value = e.target.value; })}
                >
                    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
            );
        } else {
            // many → chips
            const opts = optsFor(m);
            const chosen = Array.isArray(c.value) ? c.value.map(String) : [];
            const avail = opts.filter(([v]) => !chosen.includes(String(v)));
            inner = (
                <div className="tags">
                    {chosen.map((v) => (
                        <span className="tag" key={v}>
                            {m.rel ? relLabelFor(m.rel, v) : v}
                            <span
                                className="x" role="button" tabIndex={0}
                                onClick={() => edit((r) => { const c2 = nodeAt(r, path) as Condition; c2.value = (c2.value as string[]).filter((x) => String(x) !== v); })}
                            >
                                <X size={11} />
                            </span>
                        </span>
                    ))}
                    <select
                        value=""
                        onChange={(e) => { const v = e.target.value; if (!v) return; edit((r) => { const c2 = nodeAt(r, path) as Condition; c2.value = [...(Array.isArray(c2.value) ? c2.value : []), v]; }); }}
                    >
                        <option value="">{chosen.length ? "+ add…" : "select…"}</option>
                        {avail.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                </div>
            );
        }
        return canDyn ? <div className="dynwrap"><div className="dynwrap__inner">{inner}</div>{dynToggle(path, false)}</div> : inner;
    };

    const dynToggle = (path: number[], on: boolean) => (
        <button
            type="button" className={`dynbtn${on ? " on" : ""}`} title="Use a dynamic value ($principal.*)"
            onClick={() => edit((r) => {
                const c = nodeAt(r, path) as Condition;
                const m = walkChain(spec, model, c.field);
                c.dyn = !c.dyn;
                c.value = c.dyn ? (m.dyn ?? "$today") : defaultValue(spec, model, c.field, c.op);
            })}
        >
            <SlidersHorizontal size={15} />
        </button>
    );

    /* ---- builder group -------------------------------------------------- */
    const renderGroup = (node: Group, path: number[], depth: number): React.ReactNode => (
        <div className={`group${depth ? " depth" + depth : ""}`}>
            <div className="group__h">
                <span className="lbl">Match</span>
                <span className="joinseg">
                    {(["AND", "OR"] as const).map((j) => (
                        <button
                            key={j} type="button" data-j={j} className={node.join === j ? "on" : ""}
                            onClick={() => edit((r) => { (nodeAt(r, path) as Group).join = j; })}
                        >
                            {j === "AND" ? "ALL" : "ANY"}
                        </button>
                    ))}
                </span>
                <span className="lbl">of the following{depth ? "" : ":"}</span>
                {depth ? (
                    <button
                        type="button" className="xbtn grm" title="Remove group"
                        onClick={() => edit((r) => { const parent = nodeAt(r, path.slice(0, -1)) as Group; parent.items.splice(path[path.length - 1], 1); })}
                    >
                        <X size={14} />
                    </button>
                ) : null}
            </div>

            {node.items.length === 0 ? (
                <div className="hint emptyrow">No conditions yet.</div>
            ) : (
                node.items.map((it, i) => {
                    const cp = [...path, i];
                    if (it.kind === "group") return <div className="subgroup" key={i}>{renderGroup(it, cp, depth + 1)}</div>;
                    const m = meta(it);
                    return (
                        <div className="row" key={i}>
                            <div className="fieldcell">
                                <button type="button" className="cond-in fieldbtn" onClick={(e) => openPicker(cp, e.currentTarget)}>
                                    <span>{m.label}</span>
                                    <ChevronDown size={14} />
                                </button>
                            </div>
                            <select
                                className="cond-in" value={it.op}
                                onChange={(e) => edit((r) => {
                                    const c = nodeAt(r, cp) as Condition;
                                    c.op = e.target.value;
                                    if (opMeta(m.type, c.op).vmode === "none") c.value = null;
                                    else if (!c.dyn) c.value = defaultValue(spec, model, c.field, c.op);
                                })}
                            >
                                {opsForType(m.type).map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
                            </select>
                            <div className="valcell">{renderValue(cp, it)}</div>
                            <div className="acts">
                                <button
                                    type="button" className="xbtn"
                                    onClick={() => edit((r) => { const parent = nodeAt(r, path) as Group; parent.items.splice(i, 1); })}
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        </div>
                    );
                })
            )}

            <div className="addrow">
                <button type="button" className="addbtn" onClick={() => edit((r) => { (nodeAt(r, path) as Group).items.push(newCond()); })}>
                    <Plus size={13} /> Add condition
                </button>
                <button
                    type="button" className="addbtn group"
                    onClick={() => edit((r) => { (nodeAt(r, path) as Group).items.push({ kind: "group", join: "OR", items: [newCond()] }); })}
                >
                    <Layers size={13} /> Add group
                </button>
            </div>
        </div>
    );

    /* ---- readable preview ----------------------------------------------- */
    const renderReadable = (node: Node): React.ReactNode => {
        if (node.kind === "cond") {
            const m = meta(node);
            const om = opMeta(m.type, node.op);
            let val: React.ReactNode = null;
            if (om.vmode === "range") {
                const rv = asRange(node.value);
                val = <> <span className="val">{rv.from || "…"}</span> <span className="op">to</span> <span className="val">{rv.to || "…"}</span></>;
            } else if (om.vmode !== "none") {
                if (node.dyn) val = <> <span className="dyn">{DYN_LABEL[String(node.value)] ?? String(node.value)}</span></>;
                else if (Array.isArray(node.value)) val = <> <span className="val">{node.value.length ? node.value.map((v) => (m.rel ? relLabelFor(m.rel, String(v)) : String(v))).join(", ") : "…"}</span></>;
                else val = <> <span className="val">{m.rel ? relLabelFor(m.rel, String(node.value)) : (node.value === "" || node.value == null ? "…" : String(node.value))}</span></>;
            }
            return (
                <div className="ri">
                    <span className="bullet" />
                    <span><span className="fld">{m.label}</span> <span className="op">{om.label}</span>{val}</span>
                </div>
            );
        }
        if (!node.items.length) return <div className="ri muted">No conditions yet — matches every record.</div>;
        return (
            <div className="rg">
                <div className="rg-h"><span className={`j ${node.join}`}>{node.join === "AND" ? "ALL" : "ANY"}</span> of these are true:</div>
                <div className="rg-items">{node.items.map((c, i) => <div key={i}>{renderReadable(c)}</div>)}</div>
            </div>
        );
    };

    /* ---- code tab (read-only serialized domain) ------------------------- */
    const codeText = useMemo(() => formatDomain(domain), [domain]);

    return (
        <Dialog.Root open={open} onOpenChange={(next) => { if (!next) { setPicker(null); onClose(); } }}>
            <Dialog.Portal>
                <Dialog.Overlay className="theme-dialog__overlay" />
                <Dialog.Content className="domain-builder" aria-describedby={undefined}>
                    <div className="dlg__h">
                        <span className="dlg__icon"><Filter size={22} /></span>
                        <div className="dlg__title">
                            <Dialog.Title className="ti">Domain Builder</Dialog.Title>
                            <div className="model">Filter on <span className="mchip">{model}</span> · {spec[model]?.label ?? model}</div>
                        </div>
                        <div className="seg">
                            {([["visual", "Visual", List], ["readable", "Readable", Eye], ["code", "Code", Code]] as const).map(([k, l, Ic]) => (
                                <button key={k} type="button" className={mode === k ? "on" : ""} onClick={() => { setMode(k); setPicker(null); }}>
                                    <Ic size={14} /> {l}
                                </button>
                            ))}
                        </div>
                        <button type="button" className="iconbtn" onClick={() => { setPicker(null); onClose(); }}><X size={16} /></button>
                    </div>

                    <div className="dlg__b">
                        {targetLoading && !targetLoaded ? (
                            <div className="dlg__loading">
                                <Loader2 size={18} className="spin" /> <span>Loading {model} fields…</span>
                            </div>
                        ) : mode === "code" ? (
                            <>
                                <pre className="code">{codeText}</pre>
                                <div className="hint codehint">EDE domain (prefix notation). Dynamic values resolve from the request principal at evaluation time.</div>
                            </>
                        ) : mode === "readable" ? (
                            <div className="readable">{renderReadable(tree)}</div>
                        ) : (
                            <>
                                {renderGroup(tree, [], 0)}
                                <div className="preview">
                                    <div className="preview__h"><Eye size={13} /> Live preview</div>
                                    <div className="readable">{renderReadable(tree)}</div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="dlg__f">
                        <div className="matchcount">
                            <Check size={15} />{" "}
                            {matchCount != null
                                ? <>matches <b>{matchCount}</b> record{matchCount === 1 ? "" : "s"}</>
                                : <>{condCount} condition{condCount === 1 ? "" : "s"}</>}
                        </div>
                        <div className="spacer" />
                        <button type="button" className="btn" onClick={() => { setPicker(null); onClose(); }}>Cancel</button>
                        <button type="button" className="btn primary" onClick={() => { setPicker(null); onApply(serializeDomain(tree)); }}>Apply domain</button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>

            {picker ? <FieldChainPicker
                spec={spec} model={model} picker={picker} query={fpQuery}
                onQuery={setFpQuery}
                onDrill={(name) => setPicker((p) => {
                    if (!p) return p;
                    const next = { ...p, drill: [...p.drill, name] };
                    // Fetch the related model's spec so its fields appear.
                    ensureSpec(modelAtDrill(spec, model, next.drill));
                    return next;
                })}
                onCrumb={(i) => setPicker((p) => p ? { ...p, drill: i < 0 ? [] : p.drill.slice(0, i + 1) } : p)}
                onChoose={chooseField}
                onClose={() => setPicker(null)}
            /> : null}
        </Dialog.Root>
    );
}

/* ---- field-chain picker (portaled to body to escape dialog transform) -- */

interface PickerProps {
    spec: SpecGraph;
    model: string;
    picker: PickerState;
    query: string;
    onQuery: (q: string) => void;
    onDrill: (name: string) => void;
    onCrumb: (i: number) => void;
    onChoose: (name: string) => void;
    onClose: () => void;
}

const REL_TYPES = new Set(["m2o", "m2m", "o2m", "reference", "many2many", "one2many"]);

function FieldChainPicker({ spec, model, picker, query, onQuery, onDrill, onCrumb, onChoose, onClose }: PickerProps) {
    const curModel = modelAtDrill(spec, model, picker.drill);
    const fields = spec[curModel]?.fields ?? [];
    const q = query.toLowerCase();
    const hasRel = fields.some((f) => REL_TYPES.has(f.type));

    return createPortal(
        <div className="domain-builder-fp-root">
            <div className="domain-builder-fp-backdrop" onClick={onClose} />
            <div className="fpmenu" style={{ left: picker.x, top: picker.y + 4 }}>
                <div className="fpbar">
                    <span className="fpc" role="button" tabIndex={0} onClick={() => onCrumb(-1)}>{spec[model]?.label ?? model}</span>
                    {picker.drill.map((s, i) => {
                        const mm = modelAtDrill(spec, model, picker.drill.slice(0, i));
                        const f = spec[mm]?.fields.find((x) => x.name === s);
                        return (
                            <span key={i}>
                                <span className="fpsep">›</span>
                                <span className="fpc" role="button" tabIndex={0} onClick={() => onCrumb(i)}>{f?.label ?? s}</span>
                            </span>
                        );
                    })}
                </div>
                <div className="fpsearchwrap">
                    <Search size={14} />
                    <input
                        className="fpsearch" autoFocus placeholder={`Search ${spec[curModel]?.label ?? curModel} fields…`}
                        value={query} onChange={(e) => onQuery(e.target.value)}
                    />
                </div>
                <div className="fplist">
                    {fields
                        .filter((f) => (f.label + " " + f.name).toLowerCase().includes(q))
                        .map((f) => {
                            const rel = REL_TYPES.has(f.type);
                            const tech = [...picker.drill, f.name].join(".");
                            return (
                                <div className="fprow" key={f.name}>
                                    <button type="button" className="fpsel" onClick={() => onChoose(f.name)}>
                                        <span className="fpl">{f.label}</span>
                                        <span className="fpsub mono">{tech}</span>
                                    </button>
                                    {rel ? (
                                        <button type="button" className="fpdrill" title={`Open ${f.label} fields`} onClick={() => onDrill(f.name)}>
                                            <ChevronRight size={16} />
                                        </button>
                                    ) : null}
                                </div>
                            );
                        })}
                </div>
                <div className="fphint">
                    click a field to use it{hasRel ? <> · <ChevronRight size={11} /> opens the related fields</> : null}
                </div>
            </div>
        </div>,
        document.body,
    );
}

/* ---- helpers ---------------------------------------------------------- */

function safeParse(value: Domain): Group {
    try {
        return parseDomain(value);
    } catch {
        return { kind: "group", join: "AND", items: [] };
    }
}

const asRange = (v: Condition["value"]): RangeValue => (isRange(v) ? v : { from: "", to: "" });

/** Pretty-print an EDE domain as one operand per line (read-only Code tab). */
function formatDomain(domain: Domain): string {
    if (!domain.length) return "[]";
    const lines = domain.map((tok) => (typeof tok === "string" ? `  ${JSON.stringify(tok)}` : `  [${JSON.stringify(tok[0])}, ${JSON.stringify(tok[1])}, ${JSON.stringify(tok[2])}]`));
    return `[\n${lines.join(",\n")}\n]`;
}

// re-export Pencil so the field widget can use the same icon set without a
// second lucide import path (keeps the widget's edit-affordance consistent).
export { Pencil as EditIcon };
